import { GridContent } from '@ant-design/pro-components';
import { history } from '@umijs/max';
import { Card, InputNumber, Button, Typography, Space, Statistic, Divider, Radio, message } from 'antd';
import {
  WalletOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { calculateRecharge, getPaymentMethods, getPlans } from '@/services/payment';
import { createStyles } from 'antd-style';
import IconPayPal from '@/assets/icons/IconPayPal.svg';
import { useRequest } from '@umijs/max';
import { normalizePlanList } from '@/pages/payment/utils';

const { Title, Text } = Typography;

const useStyles = createStyles(({ token }) => ({
  container: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '24px 0',
  },
  resultCard: {
    borderRadius: token.borderRadiusLG,
    marginTop: 16,
  },
  methodIcon: {
    width: 18,
    height: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 1,
  },
}));

/** 支付方式 → 币种映射 */
const METHOD_CURRENCY: Record<string, { code: string; symbol: string }> = {
  WECHAT_NATIVE: { code: 'CNY', symbol: '¥' },
  PAYPAL: { code: 'USD', symbol: '$' },
};

const PRESETS: Record<string, number[]> = {
  CNY: [50, 100, 200, 500, 1000],
  USD: [5, 10, 20, 50, 100],
};

const methodIcons: Record<string, React.ReactNode> = {
  WECHAT_NATIVE: <WechatOutlined style={{ color: '#07c160', fontSize: 16 }} />,
  PAYPAL: (
    <img
      src={IconPayPal}
      alt="PayPal"
      width={16}
      height={16}
      style={{ display: 'block', objectFit: 'contain' }}
    />
  ),
};

const parsePositiveIntegerAmount = (value?: string) => {
  const digits = String(value || '').replace(/[^\d]/g, '');
  if (!digits) {
    return '';
  }
  const normalized = digits.replace(/^0+/, '');
  return normalized || '';
};

type StartCheckoutContext =
  | { orderType: 'SUBSCRIPTION'; planCode: string; planName?: string; billingCycle: string; durationDays?: number }
  | { orderType: 'RECHARGE'; rechargeAmount: number; currency?: string; paymentMethod?: string };

const Recharge: React.FC<{
  embedded?: boolean;
  onStartCheckout?: (ctx: StartCheckoutContext) => void;
}> = ({ embedded, onStartCheckout }) => {
  const { styles } = useStyles();
  const [paymentMethod, setPaymentMethod] = useState<string>('WECHAT_NATIVE');
  const [methods, setMethods] = useState<any[]>([]);
  const [amount, setAmount] = useState<number | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: planList } = useRequest(getPlans, {
    formatResult: normalizePlanList,
    refreshOnWindowFocus: false,
  });
  // 充值资格使用三态：
  // true: 明确允许
  // false: 明确禁止
  // undefined: 结果未返回或结构异常，此时不提前拦截，交给后端最终兜底
  const rechargeEligible = useMemo<boolean | undefined>(() => {
    if (typeof planList?.rechargeEligible === 'boolean') {
      return planList.rechargeEligible;
    }
    if (typeof planList?.paidSubscriptionActive === 'boolean') {
      return planList.paidSubscriptionActive;
    }
    if (typeof planList?.currentSubscription?.paidSubscriptionActive === 'boolean') {
      return planList.currentSubscription.paidSubscriptionActive;
    }
    return undefined;
  }, [
    planList?.rechargeEligible,
    planList?.paidSubscriptionActive,
    planList?.currentSubscription?.paidSubscriptionActive,
  ]);

  const curr = useMemo(() => METHOD_CURRENCY[paymentMethod] ?? { code: 'CNY', symbol: '¥' }, [paymentMethod]);
  const presetAmounts = useMemo(() => PRESETS[curr.code] ?? PRESETS.CNY, [curr.code]);

  useEffect(() => {
    getPaymentMethods().then((data) => {
      if (Array.isArray(data)) {
        setMethods(data);
        if (data.length > 0) setPaymentMethod(data[0].code);
      }
    }).catch(() => {
      message.error('获取支付方式失败');
    });
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  /** 切换支付方式时重置金额和积分计算 */
  const handleMethodChange = useCallback((method: string) => {
    setPaymentMethod(method);
    setAmount(null);
    setCalcResult(null);
  }, []);

  const handleCalculate = useCallback((value: number | null, currencyCode?: string) => {
    setAmount(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value <= 0 || !Number.isInteger(value)) {
      setCalcResult(null);
      return;
    }
    const calcCurrency = currencyCode ?? curr.code;
    debounceRef.current = setTimeout(async () => {
      setCalculating(true);
      try {
        const result = await calculateRecharge({ amount: value, currency: calcCurrency });
        setCalcResult(result);
      } catch (e) {
        setCalcResult(null);
      } finally {
        setCalculating(false);
      }
    }, 500);
  }, [curr.code]);

  const handleRecharge = () => {
    if (rechargeEligible === false) {
      message.warning('充值前需要先订阅非免费套餐');
      return;
    }
    if (!amount || amount <= 0) {
      message.warning('请输入充值金额');
      return;
    }
    if (!Number.isInteger(amount)) {
      message.warning('充值金额必须为大于 0 的整数');
      return;
    }
    if (embedded && onStartCheckout) {
      onStartCheckout({
        orderType: 'RECHARGE',
        rechargeAmount: amount,
        currency: curr.code,
        paymentMethod,
      });
      return;
    }
    history.push(
      `/payment/checkout?orderType=RECHARGE&rechargeAmount=${amount}&currency=${curr.code}&paymentMethod=${paymentMethod}`,
    );
  };

  const content = (
    <div
      className={styles.container}
      style={embedded ? { maxWidth: 760, padding: 0, margin: '0 auto' } : undefined}
    >
        <Title level={3} style={{ textAlign: 'center', marginBottom: 8 }}>
          充值点数
        </Title>
        <Text
          type="secondary"
          style={{ display: 'block', textAlign: 'center', marginBottom: 32 }}
        >
          充值后点数永久有效
        </Text>

        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {rechargeEligible === false && (
              <div style={{ padding: 12, borderRadius: 12, background: 'rgba(250, 173, 20, 0.12)', border: '1px solid rgba(250, 173, 20, 0.25)' }}>
                <Text strong>充值已禁用</Text>
                <div style={{ marginTop: 6 }}>
                  <Text type="secondary">请先订阅非免费套餐后再充值。</Text>
                </div>
              </div>
            )}
            {/* 支付方式 */}
            {methods.length > 1 && (
              <div>
                <Text strong>支付方式</Text>
                <div style={{ marginTop: 8 }}>
                  <Radio.Group
                    value={paymentMethod}
                    onChange={(e) => handleMethodChange(e.target.value)}
                  >
                    <Space>
                      {methods.map((m: any) => (
                        <Radio.Button key={m.code} value={m.code}>
                          <Space size={4}>
                            <span className={styles.methodIcon}>{methodIcons[m.code]}</span>
                            {m.name}
                          </Space>
                        </Radio.Button>
                      ))}
                    </Space>
                  </Radio.Group>
                </div>
              </div>
            )}
            {/* 金额输入 */}
            <div>
              <Text strong>充值金额 ({curr.code})</Text>
              <div style={{ marginTop: 8 }}>
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  min={1}
                  max={50000}
                  precision={0}
                  step={1}
                  prefix={curr.symbol}
                  placeholder="请输入大于 0 的整数金额"
                  value={amount}
                  parser={parsePositiveIntegerAmount}
                  onChange={handleCalculate}
                />
              </div>
            </div>

            <Space wrap>
              {presetAmounts.map((preset) => (
                <Button
                  key={preset}
                  type={amount === preset ? 'primary' : 'default'}
                  onClick={() => handleCalculate(preset)}
                >
                  {curr.symbol}{preset}
                </Button>
              ))}
            </Space>

            {calcResult && (
              <Card className={styles.resultCard} size="small">
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: '100%' }}
                >
                  <Statistic
                    title="基础点数"
                    value={calcResult.baseCredits}
                    prefix={<WalletOutlined />}
                    precision={2}
                  />
                  {calcResult.bonusCredits > 0 && (
                    <Statistic
                      title="赠送点数"
                      value={calcResult.bonusCredits}
                      prefix={<GiftOutlined />}
                      precision={2}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  )}
                  <Divider style={{ margin: '8px 0' }} />
                  <Statistic
                    title="到账总点数"
                    value={calcResult.totalCredits}
                    prefix={<ThunderboltOutlined />}
                    precision={2}
                    valueStyle={{ color: '#1677ff', fontWeight: 700 }}
                  />
                </Space>
              </Card>
            )}

            <Button
              type="primary"
              block
              size="large"
              disabled={rechargeEligible === false || !amount || amount <= 0}
              loading={calculating}
              onClick={handleRecharge}
            >
              去支付
            </Button>
          </Space>
        </Card>
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <GridContent>
      {content}
    </GridContent>
  );
};

export default Recharge;
